/**
 * workQueueLogic — pure state + label derivations behind the "Next up" guided
 * work-through flow (docs/crm-work-queue-plan.md). The server sends DATA
 * (reason kinds + timestamps); every recruiter-facing word is rendered here,
 * and the advance/skip/persist math lives here — React-free and directly
 * testable (the safetyNetLane pattern). No imports that reach the axios
 * client, so the tests need no api mock.
 */
import { DETECTOR_LABEL } from './pages/safetyNetLane';
import type { CoverageInfo } from './components/CoverageBadge';

export interface WorkQueueReason {
  kind: string;
  at?: string | null;
  detector?: string;
  detectors?: string[];
  attempt_count?: number | null;
  review_kind?: string;
}

export interface WorkQueueItem {
  key: string;
  href: string;
  contact_id: string;
  name: string;
  lifecycle_status: string | null;
  owner_user_id: string | null;
  study_title: string | null;
  /** The landing-form verdict the lead ARRIVED with (eligible | needs_review |
   *  pending | not_eligible | null). It is what re-ranks the queue server-side
   *  (ELIGIBILITY_SHIFT in crmWorkQueue.js), so the bar shows it: the recruiter
   *  should be able to see why this lead is the one on deck. */
  incoming_eligibility?: string | null;
  /**
   * Set ONLY when this lead is in the viewer's queue because its owner is out
   * of office today (docs/crm-out-of-office-coverage-plan.md). Ownership is not
   * transferred — the badge on the row says whose lead it really is. Absent on
   * the viewer's own work, so its presence alone is the "covering" test.
   */
  coverage?: CoverageInfo | null;
  reason: WorkQueueReason;
}

/** True when the viewer holds this row only because its owner is away. */
export const isCovering = (item: WorkQueueItem | null | undefined): boolean => !!item?.coverage;

/**
 * Own work first, covered work after — a STABLE partition, not a re-rank.
 *
 * Why a partition and not a sort key: the server has already ranked the queue
 * (urgency, then the per-lead ELIGIBILITY_SHIFT), and that ranking is the
 * product decision. All this adds is the "all else equal" rule the plan asks
 * for — when a recruiter's queue widens to cover an absent teammate, their OWN
 * book should not get pushed down the list by someone else's. Within each
 * group the server's order survives untouched, because Array#sort is required
 * to be stable (ES2019) and the comparator returns 0 for every same-group pair.
 *
 * What this deliberately CANNOT do: change which reason wins a lead. The queue
 * dedups to one row per contact SERVER-side (that is why the eligibility shift
 * is per-lead), so by the time a list reaches here every contact_id appears at
 * most once and there is nothing left to pick between. Reordering rows can
 * never reach that decision — keep it that way; a client-side dedup here would
 * hand the same lead a different reason than the bar's label says.
 */
export function orderOwnWorkFirst(items: WorkQueueItem[]): WorkQueueItem[] {
  return items
    .slice()
    .sort((a, b) => Number(isCovering(a)) - Number(isCovering(b)));
}

/**
 * Whose leads a session works, as the picker's raw selection:
 *   []                            -> EVERYONE (network-wide) — the multiselect's
 *                                    "Any" row, which is the toggle the owner
 *                                    asked for
 *   ['<uuid>', …, 'unassigned']   -> those recruiters (+ unowned leads)
 * The default selection is [me, 'unassigned'] — the CRM's historical scope.
 */
export type OwnerSelection = string[];

export const UNASSIGNED = 'unassigned';

/**
 * The server token that opts a scope into out-of-office coverage —
 * "…and whatever I'm holding for someone who is out today".
 * Parsed by backend/src/services/crmOwnerScope.js (COVERING_TOKEN).
 */
export const COVERING = 'covering';

/**
 * The `?owners=` param for a selection. Empty selection = network-wide.
 *
 * WHY `meId`: the server defaults `covering` to TRUE for a viewer's implicit
 * default scope, but to FALSE for an EXPLICIT `?owners=` list — because
 * "show me only Dana's leads" is a narrow question and silently widening it
 * would be wrong. This picker, however, ALWAYS sends an explicit list (Today
 * seeds it to [me, 'unassigned']), so without this the queue would never once
 * ask for coverage and the feature would be invisible on the guided flow —
 * while Today's own buckets, which send no owners param at all, showed the
 * covered leads. Clicking "Start working" would then skip exactly the leads
 * the page had just badged.
 *
 * So: re-assert the default. A selection that includes the viewer means "my
 * work" and therefore also means the work they are covering. A selection that
 * does NOT include them is someone else's desk, and stays literal.
 */
export const ownersParam = (selection: OwnerSelection, meId?: string | null): string => {
  if (selection.length === 0) return 'all';
  const includesMe = !!meId && selection.includes(meId);
  return (includesMe ? [...selection, COVERING] : selection).join(',');
};

/** The default selection for a viewer: their leads + unassigned. */
export const defaultOwnerSelection = (userId: string | null | undefined): OwnerSelection =>
  userId ? [userId, UNASSIGNED] : [UNASSIGNED];

/**
 * Short summary for the button/bar ("Everyone", "My leads", "Dana +1").
 * `nameOf` resolves a user id to a display name; `meId` marks the viewer.
 */
export function ownerSummary(
  selection: OwnerSelection,
  nameOf: (id: string) => string | undefined,
  meId?: string | null,
): string {
  if (selection.length === 0) return 'Everyone';
  const label = (v: string) => (v === UNASSIGNED ? 'Unassigned' : v === meId ? 'My leads' : nameOf(v) || 'Someone');
  const mineAndUnassigned = meId
    && selection.length === 2
    && selection.includes(meId)
    && selection.includes(UNASSIGNED);
  if (mineAndUnassigned) return 'My leads';
  if (selection.length === 1) return label(selection[0]);
  return `${label(selection[0])} +${selection.length - 1}`;
}

export interface WorkQueueState {
  source: 'all' | 'safety_net';
  /** The selection this session was started with — refills reuse it.
   *  null = the server's default scope (caller's leads + unassigned). */
  owners: OwnerSelection | null;
  items: WorkQueueItem[];
  index: number;
  /** contact_ids worked ("Done") this session — excluded from the refill. */
  handled: string[];
  /** contact_ids skipped this session — also excluded from the refill. */
  skipped: string[];
  startedAt: string;
}

/** Compact relative span: "25m", "3h", "4d". */
const rel = (iso: string | null | undefined, now: Date): string | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const span = Math.abs(now.getTime() - ms);
  const m = Math.round(span / 60e3);
  if (m < 60) return `${Math.max(1, m)}m`;
  const h = Math.round(span / 3600e3);
  if (h < 48) return `${h}h`;
  return `${Math.round(span / 86400e3)}d`;
};

/** The bar's one-line "why this lead is up next". */
export function reasonLabel(reason: WorkQueueReason, now: Date = new Date()): string {
  const r = rel(reason.at, now);
  switch (reason.kind) {
    case 'callback_due': {
      if (!reason.at) return 'Booked call due';
      const overdue = new Date(reason.at).getTime() <= now.getTime();
      return overdue ? `Booked call — ${r} overdue` : `Booked call in ${r}`;
    }
    case 'agent_review':
      return `Agent review — ${String(reason.review_kind || 'decision').replace(/_/g, ' ')}`;
    case 'needs_reply':
      return r ? `Owes a reply — waiting ${r}` : 'Owes a reply';
    case 'new_lead':
      return r ? `New lead — arrived ${r} ago, first touch` : 'New lead — first touch';
    case 'safety_net': {
      const label = (reason.detector && DETECTOR_LABEL[reason.detector]) || 'Left behind';
      const extra = (reason.detectors?.length || 0) - 1;
      return extra > 0 ? `${label} (+${extra} more)` : label;
    }
    case 'follow_up': {
      const attempt = reason.attempt_count ? ` — attempt ${reason.attempt_count}` : '';
      return r ? `Follow-up due ${r} ago${attempt}` : `Follow-up due${attempt}`;
    }
    case 'stale':
      return r ? `Going quiet — ${r} since last activity` : 'Going quiet';
    default:
      return 'Needs attention';
  }
}

/**
 * The incoming-verdict chip for a queue row — shown ONLY for the two verdicts
 * that actually move a lead's position (the server's ELIGIBILITY_SHIFT).
 * `needs_review`, an incomplete screener and a missing verdict are unknowns,
 * rank neutrally, and would be noise on a pill this small.
 */
export function eligibilityChip(
  incoming: string | null | undefined,
): { label: string; tone: 'good' | 'bad' } | null {
  if (incoming === 'eligible') return { label: 'Eligible', tone: 'good' };
  if (incoming === 'not_eligible') return { label: 'Not eligible', tone: 'bad' };
  return null;
}

// --- Snooze from the bar ---------------------------------------------------
//
// "Done" is session-only: it advances the bar and keeps the lead out of this
// sitting's one refill, and that is ALL — nothing reaches the server, so a lead
// you looked at and left alone is back the next time you click Work through.
// Snoozing is the durable "nothing needed here, see you later": it writes
// crm.contacts.snoozed_until, which the worklist buckets and the safety net
// both honor, and which a new inbound clears automatically so a deferred lead
// can never be buried while the patient is trying to reach us.

/**
 * When a bar snooze should end: local midnight at the START of the day `days`
 * from today, so the lead returns at the top of that morning rather than at
 * whatever moment you happened to defer it. 1 = tomorrow, 7 = a week out.
 *
 * The date is advanced BEFORE the clock is zeroed — the other order lands an
 * hour off across a DST change.
 */
export function snoozeUntilDays(days: number, now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() + Math.max(1, Math.floor(days)));
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/** The bar's two quick picks; anything else goes through the days box. */
export const SNOOZE_PICKS: { label: string; days: number }[] = [
  { label: 'Tomorrow', days: 1 },
  { label: 'Next week', days: 7 },
];

/** Parse the "put it out X days" box — a whole number of days, 1–365. */
export function parseSnoozeDays(raw: string): number | null {
  const n = Number(String(raw).trim());
  if (!Number.isInteger(n) || n < 1 || n > 365) return null;
  return n;
}

/** "Fri, Aug 15" — the day the lead comes back. */
export function snoozeReturnLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

/**
 * Why the row on deck cannot be snoozed OFF the queue, or null if it can.
 *
 * Two queue sources do not read `snoozed_until` at all, so offering a snooze
 * there would defer the lead and hand them straight back — the exact
 * frustration this button exists to fix. Blocking with the real reason beats
 * a control that silently does nothing.
 */
export function snoozeBlockedReason(kind: string): string | null {
  if (kind === 'callback_due') {
    return 'A booked call is an appointment — reschedule or cancel it on the lead instead.';
  }
  if (kind === 'agent_review') {
    return 'An agent review clears when you resolve it, not by snoozing the lead.';
  }
  return null;
}

/** How one queue row reads in the list: what you did with it, or where it sits. */
export type QueueRowStatus = 'done' | 'skipped' | 'current' | 'upcoming';

/**
 * The status of row `i`. Pure. Derived from the session ledgers first and the
 * cursor only as a fallback, so jumping around the list (goTo) can't relabel
 * rows you never actually touched.
 */
export function rowStatus(s: WorkQueueState, i: number): QueueRowStatus {
  const item = s.items[i];
  if (!item) return 'upcoming';
  if (i === s.index) return 'current';
  if (s.handled.includes(item.contact_id)) return 'done';
  if (s.skipped.includes(item.contact_id)) return 'skipped';
  return 'upcoming';
}

/** Rows worked so far — the list's "3 of 17 done" line. */
export const doneCount = (s: WorkQueueState | null): number => (s ? s.handled.length : 0);

export const currentItem = (s: WorkQueueState | null): WorkQueueItem | null =>
  s ? s.items[s.index] ?? null : null;

export const progress = (s: WorkQueueState | null): { position: number; total: number } =>
  s ? { position: Math.min(s.index + 1, s.items.length), total: s.items.length } : { position: 0, total: 0 };

/** "Done" on the current item: remember it as handled, move on. */
export function advanceState(s: WorkQueueState): WorkQueueState {
  const cur = currentItem(s);
  return {
    ...s,
    handled: cur && !s.handled.includes(cur.contact_id) ? [...s.handled, cur.contact_id] : s.handled,
    index: s.index + 1,
  };
}

/** "Skip": same move, different ledger (skips are not recycled by the refill). */
export function skipState(s: WorkQueueState): WorkQueueState {
  const cur = currentItem(s);
  return {
    ...s,
    skipped: cur && !s.skipped.includes(cur.contact_id) ? [...s.skipped, cur.contact_id] : s.skipped,
    index: s.index + 1,
  };
}

/** Leads this session already touched — the refill filters them out. */
export const sessionExcludes = (s: WorkQueueState): Set<string> =>
  new Set([...s.handled, ...s.skipped]);

/** Swap in a refill list (already exclude-filtered), restarting at the top.
 *  Applies the own-work-first partition, so a refill that pulls in an absent
 *  teammate's leads does not bury the viewer's own. */
export const withRefill = (s: WorkQueueState, items: WorkQueueItem[]): WorkQueueState =>
  ({ ...s, items: orderOwnWorkFirst(items), index: 0 });

// --- sessionStorage persistence (per-tab; survives refresh mid-run) --------

const STORE_VERSION = 1;

export function serializeState(s: WorkQueueState): string {
  return JSON.stringify({ v: STORE_VERSION, state: s });
}

export function parseStoredState(raw: string | null): WorkQueueState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.v !== STORE_VERSION) return null;
    const s = parsed.state;
    if (!s || !Array.isArray(s.items) || typeof s.index !== 'number') return null;
    return s as WorkQueueState;
  } catch {
    return null;
  }
}
